import React, { useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

const AVATARS = [
  "https://images.unsplash.com/photo-1573633525347-963f98290d1e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBidXNpbmVzcyUyMHBlb3BsZXxlbnwwfHx8fDE3NzgyODAzNTB8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1573633509389-0e3075dea01b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBidXNpbmVzcyUyMHBlb3BsZXxlbnwwfHx8fDE3NzgyODAzNTB8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1573633525347-963f98290d1e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBidXNpbmVzcyUyMHBlb3BsZXxlbnwwfHx8fDE3NzgyODAzNTB8MA&ixlib=rb-4.1.0&q=85",
];

export default function Testimonials() {
  const { t } = useT();
  const [active, setActive] = useState(0);
  const items = t.testimonials.items;
  const tItem = items[active];
  const avatar = AVATARS[active];

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative bg-[#F8F9FA] py-28 lg:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="overline">{t.testimonials.overline}</span>
              <div className="h-px w-16 bg-slate-300" />
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.025em] leading-[1.02] text-slate-900">
              {t.testimonials.title1}<br />
              <span className="text-slate-400">{t.testimonials.title2}</span>
            </h2>
          </motion.div>
        </div>

        {/* Featured testimonial */}
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center bg-white rounded-2xl p-8 lg:p-14 border border-slate-200"
          data-testid="featured-testimonial"
        >
          <div className="lg:col-span-7 order-2 lg:order-1">
            <Quote className="w-10 h-10 text-[#FF6B00] mb-6" strokeWidth={2} />
            <blockquote
              className="font-display text-2xl lg:text-3xl leading-snug tracking-tight text-slate-900 font-medium"
              dangerouslySetInnerHTML={{ __html: `« ${tItem.quote} »` }}
            />
            <div className="flex items-center gap-1 mt-8">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className="w-4 h-4 fill-[#FF6B00] text-[#FF6B00]"
                />
              ))}
            </div>
            <div className="mt-6">
              <div className="font-display font-semibold text-slate-900 text-lg">
                {tItem.name}
              </div>
              <div className="text-sm text-slate-500">
                {tItem.role} · {tItem.company}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2 relative">
            <div className="aspect-square overflow-hidden rounded-2xl bg-slate-200 max-w-sm mx-auto">
              <img
                src={avatar}
                alt={tItem.name}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            {/* Decorative ring */}
            <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full border border-[#005BEA]/30 hidden lg:block" />
          </div>
        </motion.div>

        {/* Navigation */}
        <div className="mt-10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                aria-label={`${t.testimonials.dotAria} ${i + 1}`}
                data-testid={`testimonial-dot-${i}`}
                className={`h-2 rounded-full transition-all ${
                  active === i
                    ? "w-8 bg-[#005BEA]"
                    : "w-2 bg-slate-300 hover:bg-slate-400"
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              data-testid="testimonial-prev"
              onClick={() =>
                setActive((a) => (a - 1 + items.length) % items.length)
              }
              aria-label={t.testimonials.prevAria}
              className="w-11 h-11 rounded-full border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-slate-700" />
            </button>
            <button
              data-testid="testimonial-next"
              onClick={() => setActive((a) => (a + 1) % items.length)}
              aria-label={t.testimonials.nextAria}
              className="w-11 h-11 rounded-full bg-slate-900 hover:bg-slate-800 text-white flex items-center justify-center transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
